"use client";

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FONT_BASTION } from "@/lib/bastion-button";
import { formatDayMonthYear, formatReturn } from "@/lib/macro/ai-vs-non-ai-format";
import { WATERMARK_BRAND_NAME } from "@/lib/watermark-badge";
import "./ReturnLinesChart.css";

// The approved post-chart look (scripts/post-charts/render.mjs, type "line"):
// Heatmap share-card header with the metric in gold, a colored line for the
// point of the chart, grey for the rest, and each line's value at its end.
const X_AXIS_HEIGHT = 30;
const DAY_MS = 86_400_000;

const LAYOUT = {
  wide: { margin: { top: 14, right: 150, bottom: 0, left: 0 }, yAxis: 58, tick: 13, labelGap: 46, valueOffset: 16 },
  compact: { margin: { top: 10, right: 92, bottom: 0, left: 0 }, yAxis: 46, tick: 11, labelGap: 34, valueOffset: 11 },
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export type ReturnSeries = {
  key: string;
  /** End label and tooltip name, e.g. "AI (44 stocks)". */
  name: string;
  /** End label on phones, when `name` is too long for the narrow label column. */
  shortName?: string;
  color: string;
  /** Color of the end value: the line color for highlighted series, white for the rest. */
  valueColor: string;
  /** Drawn thicker and on top. */
  focus?: boolean;
};

export type ReturnPoint = {
  date: string;
  /** % change from the first point, by series key. */
  values: Record<string, number>;
};

type ChartPoint = Record<string, number | string> & { date: string; ts: number };

function niceStep(span: number): number {
  const raw = span / 5;
  return [5, 10, 20, 25, 50, 100, 200, 250, 500].find((step) => step >= raw) ?? 1000;
}

function useCompact(): boolean {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 640px)");
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return compact;
}

/** End labels need the plot height, which only the rendered canvas knows. */
function useHeight<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setHeight(entry.contentRect.height));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, height] as const;
}

/** Cumulative % return lines with the share-card header and end labels. Series order = legend order; the first draws on top. */
export default function ReturnLinesChart({
  universe,
  flag,
  metric,
  series,
  points,
  startDate,
  endDate,
  endLabelWidth,
}: {
  universe: string;
  flag: string;
  metric: string;
  series: ReturnSeries[];
  points: ReturnPoint[];
  startDate: string;
  endDate: string;
  /** Room right of the plot for the end labels, when the names are longer than "Non-AI (456)". */
  endLabelWidth?: { wide: number; compact: number };
}) {
  const compact = useCompact();
  const layout = compact ? LAYOUT.compact : LAYOUT.wide;
  const margin = { ...layout.margin, right: endLabelWidth?.[compact ? "compact" : "wide"] ?? layout.margin.right };
  const [canvasRef, canvasHeight] = useHeight<HTMLDivElement>();

  const data = useMemo<ChartPoint[]>(
    () => points.map((point) => ({ ...point.values, date: point.date, ts: Date.parse(`${point.date}T00:00:00Z`) })),
    [points]
  );
  const latest = points.at(-1);

  const { domain, ticks } = useMemo(() => {
    const values = points.flatMap((point) => series.map((line) => point.values[line.key] ?? 0));
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const step = niceStep(max - min);
    const lo = Math.floor(min / step) * step;
    const hi = Math.ceil(max / step) * step;
    const yTicks: number[] = [];
    for (let value = lo; value <= hi; value += step) yTicks.push(value);
    return { domain: [lo, hi] as [number, number], ticks: yTicks };
  }, [points, series]);

  // First day of each month, skipping one that would sit on the line ends.
  const xTicks = useMemo(() => {
    if (data.length === 0) return [];
    const start = data[0].ts;
    const end = data[data.length - 1].ts;
    const cursor = new Date(start);
    cursor.setUTCDate(1);
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    const out: number[] = [];
    while (cursor.getTime() < end - 10 * DAY_MS) {
      out.push(cursor.getTime());
      cursor.setUTCMonth(cursor.getUTCMonth() + (compact ? 2 : 1));
    }
    return out;
  }, [data, compact]);

  const formatMonth = (ts: number) => {
    const date = new Date(ts);
    const month = date.getUTCMonth();
    return month === 0 ? `${MONTHS[0]} ${String(date.getUTCFullYear()).slice(2)}` : MONTHS[month];
  };

  // Labels sit on their line's last value; a lower one moves down when two would
  // touch, and the stack moves up if that pushes it past the plot.
  const ends = useMemo(() => {
    if (!latest || canvasHeight === 0) return [];
    const plotBottom = canvasHeight - margin.bottom - X_AXIS_HEIGHT;
    const plotHeight = plotBottom - margin.top;
    const toY = (value: number) => margin.top + ((domain[1] - value) / (domain[1] - domain[0] || 1)) * plotHeight;
    const list = series
      .map((line) => {
        const value = latest.values[line.key] ?? 0;
        const label = compact && line.shortName ? line.shortName : line.name;
        return { ...line, label, value, dotY: toY(value), labelY: toY(value) };
      })
      .sort((a, b) => a.dotY - b.dotY);
    for (let i = 1; i < list.length; i++) {
      if (list[i].labelY - list[i - 1].labelY < layout.labelGap) list[i].labelY = list[i - 1].labelY + layout.labelGap;
    }
    const overflow = (list.at(-1)?.labelY ?? 0) - plotBottom;
    if (overflow > 0) {
      const room = Math.max(0, (list[0]?.labelY ?? 0) - layout.valueOffset);
      const shift = Math.min(overflow, room);
      for (const end of list) end.labelY -= shift;
    }
    return list;
  }, [latest, canvasHeight, layout, margin.top, margin.bottom, domain, series, compact]);

  const axisTick = {
    fill: "rgba(255,255,255,0.45)",
    fontFamily: FONT_BASTION,
    fontSize: layout.tick,
  };

  return (
    <div className="returns-chart">
      <div className="returns-chart__head">
        <div className="returns-chart__title">
          <span className="returns-chart__flag" aria-hidden="true">
            {flag}
          </span>
          <span>{universe}</span>
          <span className="returns-chart__sep" aria-hidden="true">
            |
          </span>
          <span className="returns-chart__metric">{metric}</span>
        </div>
        <div className="returns-chart__brand">
          <img src="/iconb-mark.png" alt="" width={29} height={32} />
          {WATERMARK_BRAND_NAME}
        </div>
      </div>
      <div className="returns-chart__sub">
        Equal-weighted price return, weekly closes, {formatDayMonthYear(startDate)} to{" "}
        {formatDayMonthYear(endDate)}
      </div>

      <div className="returns-chart__canvas" ref={canvasRef}>
        <ResponsiveContainer>
          <LineChart data={data} margin={margin}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={[data[0]?.ts ?? 0, data.at(-1)?.ts ?? 0]}
              ticks={xTicks}
              interval={0}
              tickFormatter={formatMonth}
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              height={X_AXIS_HEIGHT}
            />
            <YAxis
              domain={domain}
              ticks={ticks}
              interval={0}
              tickFormatter={(value: number) => formatReturn(value, 0)}
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={layout.yAxis}
            />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.28)" />
            <Tooltip
              cursor={{ stroke: "rgba(255,255,255,0.18)", strokeWidth: 1 }}
              content={<ReturnsTooltip series={series} />}
              isAnimationActive={false}
              wrapperStyle={{ outline: "none", pointerEvents: "none" }}
            />
            {[...series].reverse().map((line) => (
              <Line
                key={line.key}
                type="linear"
                dataKey={line.key}
                name={line.name}
                stroke={line.color}
                strokeWidth={line.focus ? (compact ? 3 : 3.5) : compact ? 2.5 : 3}
                strokeLinejoin="round"
                strokeLinecap="round"
                dot={false}
                activeDot={{ r: 4, fill: line.color, stroke: "#1a1a1a", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        {ends.map((end) => (
          <Fragment key={end.key}>
            <span
              className="returns-chart__dot"
              style={{ right: margin.right - 5, top: end.dotY - 5, background: end.color }}
            />
            <div
              className="returns-chart__end"
              style={{ left: `calc(100% - ${margin.right - 16}px)`, top: end.labelY - layout.valueOffset }}
            >
              <div className="returns-chart__end-value" style={{ color: end.valueColor }}>
                {formatReturn(end.value)}
              </div>
              <div className="returns-chart__end-name">{end.label}</div>
            </div>
          </Fragment>
        ))}
      </div>

      <p className="returns-chart__foot">Source: investmentbastion.com</p>
    </div>
  );
}

function ReturnsTooltip({
  active,
  payload,
  series,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
  series: ReturnSeries[];
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="returns-tooltip">
      <div className="returns-tooltip__title">Week of {formatDayMonthYear(point.date)}</div>
      {series.map((line) => (
        <div key={line.key} className="returns-tooltip__row">
          <span className="returns-tooltip__label">
            <span className="returns-tooltip__swatch" style={{ background: line.color }} />
            {line.name}
          </span>
          <span className="returns-tooltip__value">{formatReturn(Number(point[line.key]))}</span>
        </div>
      ))}
    </div>
  );
}
